/**
 * StatusFilterBody Component
 *
 * Renders Active / Inactive status icon chips inside the FiltersModal body
 * for the Status tab. Each chip toggles its status in/out of the selected set.
 */

// React
import { useMemo } from 'react';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { useTranslation } from '../../../hooks/useTranslation';
import { CHIP_UNSELECTED, CHIP_SELECTED } from './filterChipStyles';

// Icons
import { StatusActiveIcon, StatusInactiveIcon } from '../../icons/StatusIcons';

/* ---- Layout constants ---- */
const GRID_CLASSES = 'grid grid-cols-4 gap-4';

/* ---- Chip classes ---- */
const CHIP_BASE = 'flex flex-col items-center justify-center gap-[3px] h-[66px] border rounded-input cursor-pointer select-none outline-none transition-[color,background-color,border-color,transform,opacity] ease-in-out active:scale-95 focus-visible:border-brand-yellow font-inter text-sm font-normal leading-normal text-center';

/* ---- Types ---- */
interface StatusFilterBodyProps {
  selectedStatuses: string[];
  onToggle: (status: string) => void;
}

interface StatusChip {
  id: string;
  labelKey: string;
  Icon: React.ComponentType<{ className?: string }>;
}

/* ---- Data ---- */
// Shared with the page's Status chips so the modal and the applied-filter chip
// can't drift apart in copy.
export const STATUS_FILTER_LABEL_KEYS: Record<string, string> = {
  active: 'filters.active',
  inactive: 'filters.inactive',
};

const STATUS_CHIPS: StatusChip[] = [
  { id: 'active', labelKey: STATUS_FILTER_LABEL_KEYS.active, Icon: StatusActiveIcon },
  { id: 'inactive', labelKey: STATUS_FILTER_LABEL_KEYS.inactive, Icon: StatusInactiveIcon },
];

/* ---- Component ---- */
export function StatusFilterBody({ selectedStatuses, onToggle }: StatusFilterBodyProps) {
  const { t } = useTranslation();
  const selectedSet = useMemo(() => new Set(selectedStatuses), [selectedStatuses]);

  return (
    <div className={GRID_CLASSES}>
      {STATUS_CHIPS.map(({ id, labelKey, Icon }) => {
        const isSelected = selectedSet.has(id);
        const label = t(labelKey);
        return (
          <button
            key={id}
            type="button"
            role="checkbox"
            aria-checked={isSelected}
            aria-label={label}
            className={cn(CHIP_BASE, isSelected ? CHIP_SELECTED : CHIP_UNSELECTED)}
            onClick={() => onToggle(id)}
          >
            <Icon className="size-[14px]" />
            {label}
          </button>
        );
      })}
    </div>
  );
}
